import type { FunnelPage } from "@/contexts/FunnelContext";
import { getFontsForNiche } from "@/data/fontConfig";

export type OfficialDraftModule = {
  id: string
  type: string
  variant?: string
  data: any
  design?: any
  style?: any
  status: number
  sortOrder: number
}

export type OfficialDraftColumn = {
  id: string
  span: number
  modules: OfficialDraftModule[]
}

export type OfficialDraftRow = {
  id: string
  name: string
  props: {
    backgroundColor?: 'auto' | 'light' | 'dark' | 'color' | 'gradient' | 'image'
    backgroundColorValue?: string
    backgroundGradient?: string
    backgroundImage?: string
    fullWidth?: boolean
    maxWidth?: string
    paddingX?: string
    paddingY?: string
    backgroundSize?: string
    backgroundPosition?: string
    colorScheme?: 'auto' | 'light' | 'dark'
  }
  columns: OfficialDraftColumn[]
}

export interface OfficialTemplatePage {
  name: string
  type: 'attraction' | 'landing' | 'checkout' | 'thank-you'
  path?: string | null
  settings?: Record<string, any>

  // nuevo formato real
  rows?: OfficialDraftRow[]

  // fallback temporal para migración
  sections?: any[]
}

export interface FunnelTemplate {
  id: string
  name: string
  description: string
  category: string
  icon: string
  gradient: string
  tags: string[]
  pages: OfficialTemplatePage[]
  suggestedHeadingFont?: string
  suggestedBodyFont?: string
}

export const funnelTemplates: FunnelTemplate[] = [
  // ─── 1. VENTA DE CURSO ───────────────────────────────────────────
  {
    id: "course-sales",
    name: "Venta de Curso Online",
    description: "Embudo completo para vender cursos digitales con página de captura, VSL, precios y checkout.",
    category: "Cursos",
    icon: "🎓",
    gradient: "from-violet-600 to-indigo-600",
    tags: ["Educación", "Info-producto", "E-learning"],
    suggestedHeadingFont: "Playfair Display, serif",
    suggestedBodyFont: "Inter, sans-serif",
    pages: [
      {
        type: "attraction",
        name: "Página de Captura",
        sections: [
          {
            id: "cs-s1",
            name: "Navbar",
            blocks: [{ id: "cs-b1", type: "navbar", data: { logo: "Mi Academia", links: ["Programa", "Testimonios", "Inscríbete"] } }],
            backgroundColor: "dark",
          },
          {
            id: "cs-s2",
            name: "Hero",
            blocks: [{
              id: "cs-b2", type: "hero", data: {
                title: "Domina las habilidades que transformarán tu carrera profesional",
                subtitle: "Programa intensivo de 8 semanas con mentoría personalizada, acceso de por vida y certificación incluida.",
                ctaText: "Quiero Inscribirme →",
                imageUrl: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&auto=format&fit=crop"
              }
            }],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(260, 60%, 15%) 0%, hsl(230, 70%, 20%) 100%)",
          },
          {
            id: "cs-s3",
            name: "Video Presentación",
            blocks: [{
              id: "cs-b3", type: "video", data: {
                title: "Mira lo que aprenderás en este programa",
                videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
                provider: "youtube"
              }
            }],
            backgroundColor: "auto",
          },
          {
            id: "cs-s4",
            name: "Beneficios",
            blocks: [{
              id: "cs-b4", type: "benefits", data: {
                title: "¿Por qué elegir este curso?",
                benefits: [
                  "Aprende a tu propio ritmo con acceso 24/7 a todo el material",
                  "Mentoría 1-a-1 con expertos de la industria cada semana",
                  "Proyectos reales para tu portafolio profesional",
                  "Comunidad exclusiva de más de 5,000 alumnos activos",
                  "Certificación reconocida en la industria",
                  "Garantía de devolución de 30 días sin preguntas"
                ]
              }
            }],
            backgroundColor: "auto",
          },
          {
            id: "cs-s5",
            name: "Temario",
            blocks: [{
              id: "cs-b5", type: "timeline", data: {
                title: "Contenido del Programa",
                items: [
                  { title: "Semana 1-2: Fundamentos", description: "Bases sólidas y conceptos core que necesitas dominar." },
                  { title: "Semana 3-4: Práctica Guiada", description: "Ejercicios prácticos con feedback personalizado." },
                  { title: "Semana 5-6: Proyecto Real", description: "Construye un proyecto de portafolio con mentoría." },
                  { title: "Semana 7-8: Avanzado + Certificación", description: "Técnicas avanzadas y examen de certificación." }
                ]
              }
            }],
            backgroundColor: "light",
          },
          {
            id: "cs-s6",
            name: "Testimonios",
            blocks: [{
              id: "cs-b6", type: "testimonials", data: {
                title: "Lo que dicen nuestros alumnos",
                testimonials: [
                  { name: "María García", role: "Diseñadora UX", content: "Este curso cambió mi carrera por completo. En 3 meses conseguí un aumento del 40% en mi salario.", rating: 5 },
                  { name: "Carlos López", role: "Desarrollador Frontend", content: "La mentoría personalizada marca la diferencia. Nunca había aprendido tanto en tan poco tiempo.", rating: 5 },
                  { name: "Ana Rodríguez", role: "Product Manager", content: "Excelente contenido y comunidad increíble. Lo recomiendo al 100%.", rating: 5 }
                ]
              }
            }],
            backgroundColor: "auto",
          },
          {
            id: "cs-s7",
            name: "Estadísticas",
            blocks: [{
              id: "cs-b7", type: "stats", data: {
                stats: [
                  { value: "5,000+", label: "Alumnos graduados" },
                  { value: "98%", label: "Tasa de satisfacción" },
                  { value: "4.9/5", label: "Calificación promedio" },
                  { value: "85%", label: "Consiguieron empleo" }
                ]
              }
            }],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(260, 60%, 15%) 0%, hsl(230, 70%, 20%) 100%)",
          },
          {
            id: "cs-s8",
            name: "FAQ",
            blocks: [{
              id: "cs-b8", type: "faq", data: {
                title: "Preguntas Frecuentes",
                faqs: [
                  { question: "¿Necesito experiencia previa?", answer: "No, el curso está diseñado desde cero. Solo necesitas ganas de aprender." },
                  { question: "¿Cuánto tiempo dura el acceso?", answer: "Tienes acceso de por vida a todo el material, incluyendo actualizaciones futuras." },
                  { question: "¿Hay garantía de devolución?", answer: "Sí, ofrecemos 30 días de garantía. Si no estás satisfecho, te devolvemos el 100%." },
                  { question: "¿Incluye certificación?", answer: "Sí, al completar el curso recibirás un certificado digital verificable." }
                ]
              }
            }],
            backgroundColor: "auto",
          },
        ],
      },
      {
        type: "landing",
        name: "Página de Precios",
        sections: [
          {
            id: "cs-p2-s1",
            name: "Precios",
            blocks: [{
              id: "cs-p2-b1", type: "pricing", data: {
                title: "Elige tu plan y comienza hoy",
                plans: [
                  { name: "Básico", price: "$97", period: "pago único", features: ["Acceso al curso completo", "Comunidad de alumnos", "Certificado digital"], ctaText: "Empezar", highlighted: false },
                  { name: "Premium", price: "$197", period: "pago único", features: ["Todo del plan Básico", "4 sesiones de mentoría 1-a-1", "Proyecto con feedback", "Acceso a masterclasses"], ctaText: "Inscribirme Ahora", highlighted: true },
                  { name: "VIP", price: "$497", period: "pago único", features: ["Todo del plan Premium", "8 sesiones de mentoría", "Revisión de CV", "Bolsa de empleo exclusiva"], ctaText: "Quiero ser VIP", highlighted: false }
                ]
              }
            }],
            backgroundColor: "auto",
          },
          {
            id: "cs-p2-s2",
            name: "CTA Final",
            blocks: [{
              id: "cs-p2-b2", type: "cta", data: {
                title: "No dejes pasar esta oportunidad",
                description: "Las inscripciones cierran en pocos días. Asegura tu lugar ahora.",
                buttonText: "Inscribirme Ahora →",
                buttonLink: "#checkout"
              }
            }],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(260, 70%, 25%) 0%, hsl(280, 60%, 30%) 100%)",
          },
        ],
      },
      {
        type: "thank-you",
        name: "Página de Gracias",
        sections: [
          {
            id: "cs-p3-s1",
            name: "Confirmación",
            blocks: [{
              id: "cs-p3-b1", type: "hero", data: {
                title: "¡Felicidades! Ya eres parte del programa 🎉",
                subtitle: "Te hemos enviado un correo con los detalles de acceso. Revisa tu bandeja de entrada.",
                ctaText: "Acceder al Curso",
              }
            }],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(140, 60%, 15%) 0%, hsl(160, 50%, 20%) 100%)",
          },
        ],
      },
    ],
  },

  // ─── 2. WEBINAR ──────────────────────────────────────────────────
  {
    id: "webinar",
    name: "Webinar de Alto Impacto",
    description: "Embudo para registrar asistentes a un webinar con urgencia, prueba social y seguimiento.",
    category: "Webinar",
    icon: "🎥",
    gradient: "from-rose-600 to-pink-600",
    tags: ["En vivo", "Masterclass", "Presentación"],
    suggestedHeadingFont: "Bebas Neue, sans-serif",
    suggestedBodyFont: "Montserrat, sans-serif",
    pages: [
      {
        type: "attraction",
        name: "Registro al Webinar",
        sections: [
          {
            id: "wb-s1",
            name: "Navbar",
            blocks: [{ id: "wb-b1", type: "navbar", data: { logo: "MasterClass Live", links: ["Programa", "Instructor", "Registrarme"] } }],
            backgroundColor: "dark",
          },
          {
            id: "wb-s2",
            name: "Hero con Countdown",
            blocks: [
              {
                id: "wb-b2", type: "hero", data: {
                  title: "Masterclass GRATIS: Los 3 secretos para escalar tu negocio digital en 2025",
                  subtitle: "Descubre la estrategia exacta que usaron nuestros alumnos para generar $50,000+ en 90 días. Martes 15 de Abril a las 7:00 PM.",
                  ctaText: "Reservar Mi Lugar GRATIS →",
                }
              },
              {
                id: "wb-b2b", type: "countdown", data: {
                  title: "El webinar comienza en:",
                  targetDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                  expiredMessage: "¡El evento ya comenzó!"
                }
              }
            ],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(340, 70%, 15%) 0%, hsl(320, 60%, 20%) 100%)",
          },
          {
            id: "wb-s3",
            name: "Qué aprenderás",
            blocks: [{
              id: "wb-b3", type: "features", data: {
                title: "En esta masterclass descubrirás:",
                features: [
                  { icon: "🎯", title: "El Framework de $50K", description: "El sistema paso a paso para crear ofertas irresistibles que se venden solas." },
                  { icon: "📈", title: "Automatización Inteligente", description: "Cómo configurar embudos que trabajan por ti 24/7 sin necesidad de equipo." },
                  { icon: "💰", title: "Estrategia de Tráfico", description: "Las 3 fuentes de tráfico más rentables para negocios digitales en 2025." },
                  { icon: "🚀", title: "Escala Sin Límites", description: "El método para pasar de $5K a $50K mensuales sin burnout." }
                ]
              }
            }],
            backgroundColor: "auto",
          },
          {
            id: "wb-s4",
            name: "Instructor",
            blocks: [{
              id: "wb-b4", type: "hero", data: {
                title: "Tu instructor: [Nombre del Experto]",
                subtitle: "Emprendedor serial con +10 años de experiencia, ha generado más de $2M en ventas digitales y ha entrenado a más de 10,000 emprendedores.",
                imageUrl: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&auto=format&fit=crop",
                ctaText: "Quiero Aprender de Él →",
              }
            }],
            backgroundColor: "light",
          },
          {
            id: "wb-s5",
            name: "Prueba Social",
            blocks: [{
              id: "wb-b5", type: "stats", data: {
                stats: [
                  { value: "10,000+", label: "Asistentes en webinars anteriores" },
                  { value: "97%", label: "Recomendarían este evento" },
                  { value: "$2M+", label: "Generados por alumnos" },
                  { value: "45 min", label: "De contenido puro" }
                ]
              }
            }],
            backgroundColor: "auto",
          },
          {
            id: "wb-s6",
            name: "Formulario de Registro",
            blocks: [{
              id: "wb-b6", type: "form", data: {
                title: "Reserva tu lugar ahora (GRATIS)",
                subtitle: "Los lugares son limitados. Regístrate antes de que se agoten.",
                ctaText: "Reservar Mi Lugar →",
                fields: ["Nombre completo", "Email", "WhatsApp"]
              }
            }],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(340, 70%, 15%) 0%, hsl(320, 60%, 20%) 100%)",
          },
          {
            id: "wb-s7",
            name: "Testimonios",
            blocks: [{
              id: "wb-b7", type: "testimonials", data: {
                title: "Resultados de asistentes anteriores",
                testimonials: [
                  { name: "Laura Méndez", role: "Coach de Vida", content: "Después del webinar implementé el framework y en 60 días facturé $15,000. ¡Increíble!", rating: 5 },
                  { name: "Roberto Silva", role: "Consultor", content: "El mejor webinar gratuito al que he asistido. Contenido de altísimo valor.", rating: 5 },
                  { name: "Patricia Vega", role: "Emprendedora", content: "Cambió completamente mi perspectiva del negocio digital. 100% recomendado.", rating: 5 }
                ]
              }
            }],
            backgroundColor: "auto",
          },
        ],
      },
      {
        type: "thank-you",
        name: "Confirmación de Registro",
        sections: [
          {
            id: "wb-p2-s1",
            name: "Confirmación",
            blocks: [
              {
                id: "wb-p2-b1", type: "hero", data: {
                  title: "¡Estás registrado! 🎉",
                  subtitle: "Te enviamos un correo con el enlace de acceso al webinar. Agrégalo a tu calendario para no olvidarlo.",
                  ctaText: "Agregar a Google Calendar",
                }
              },
              {
                id: "wb-p2-b2", type: "cta", data: {
                  title: "Mientras esperas el webinar...",
                  description: "Comparte con un amigo emprendedor que también necesite esta información.",
                  buttonText: "Compartir por WhatsApp",
                  buttonLink: "#"
                }
              }
            ],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(140, 60%, 15%) 0%, hsl(160, 50%, 20%) 100%)",
          },
        ],
      },
    ],
  },

  // ─── 3. RETO / CHALLENGE ─────────────────────────────────────────
  {
    id: "challenge",
    name: "Reto de 5 Días",
    description: "Embudo para lanzar un reto gratuito que engancha, educa y convierte participantes en clientes.",
    category: "Retos",
    icon: "🏆",
    gradient: "from-amber-500 to-orange-600",
    tags: ["Challenge", "Comunidad", "Engagement"],
    suggestedHeadingFont: "Oswald, sans-serif",
    suggestedBodyFont: "Roboto, sans-serif",
    pages: [
      {
        type: "attraction",
        name: "Página del Reto",
        sections: [
          {
            id: "ch-s1",
            name: "Navbar",
            blocks: [{ id: "ch-b1", type: "navbar", data: { logo: "Reto 5 Días", links: ["El Reto", "Días", "Testimonios", "Unirme"] } }],
            backgroundColor: "dark",
          },
          {
            id: "ch-s2",
            name: "Hero del Reto",
            blocks: [{
              id: "ch-b2", type: "hero", data: {
                title: "RETO GRATUITO: 5 Días para lanzar tu primer producto digital",
                subtitle: "Únete a miles de emprendedores que transformarán su idea en un producto listo para vender. Sin experiencia previa, sin inversión inicial.",
                ctaText: "¡Me Uno al Reto! →",
                imageUrl: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&auto=format&fit=crop"
              }
            }],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(30, 80%, 15%) 0%, hsl(15, 70%, 20%) 100%)",
          },
          {
            id: "ch-s3",
            name: "Countdown",
            blocks: [{
              id: "ch-b3", type: "countdown", data: {
                title: "El reto comienza en:",
                targetDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
                expiredMessage: "¡El reto ya comenzó!"
              }
            }],
            backgroundColor: "auto",
          },
          {
            id: "ch-s4",
            name: "Programa del Reto",
            blocks: [{
              id: "ch-b4", type: "timeline", data: {
                title: "Qué harás cada día",
                items: [
                  { title: "Día 1: Define tu Idea Ganadora", description: "Identifica tu nicho rentable y valida tu idea de producto digital." },
                  { title: "Día 2: Crea tu Contenido", description: "Estructura y crea el contenido de tu producto en tiempo récord." },
                  { title: "Día 3: Diseña tu Oferta", description: "Aprende a crear una oferta irresistible con bonus y garantía." },
                  { title: "Día 4: Construye tu Embudo", description: "Monta tu página de venta y sistema de cobro paso a paso." },
                  { title: "Día 5: ¡Lanza y Vende!", description: "Estrategias de lanzamiento para conseguir tus primeras ventas." }
                ]
              }
            }],
            backgroundColor: "light",
          },
          {
            id: "ch-s5",
            name: "Qué incluye",
            blocks: [{
              id: "ch-b5", type: "features", data: {
                title: "Todo lo que obtienes GRATIS",
                features: [
                  { icon: "📹", title: "5 Clases en Vivo", description: "Sesiones diarias de 60 min con ejercicios prácticos." },
                  { icon: "📋", title: "Workbooks Descargables", description: "Plantillas y guías para cada día del reto." },
                  { icon: "👥", title: "Comunidad Privada", description: "Grupo exclusivo para compartir avances y networking." },
                  { icon: "🎁", title: "Bonus Sorpresa", description: "Regalo especial para quienes completen los 5 días." }
                ]
              }
            }],
            backgroundColor: "auto",
          },
          {
            id: "ch-s6",
            name: "Estadísticas",
            blocks: [{
              id: "ch-b6", type: "stats", data: {
                stats: [
                  { value: "3,200+", label: "Participantes en retos anteriores" },
                  { value: "78%", label: "Completaron el reto" },
                  { value: "450+", label: "Productos lanzados" },
                  { value: "$180K+", label: "Generados por participantes" }
                ]
              }
            }],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(30, 80%, 15%) 0%, hsl(15, 70%, 20%) 100%)",
          },
          {
            id: "ch-s7",
            name: "Testimonios",
            blocks: [{
              id: "ch-b7", type: "testimonials", data: {
                title: "Historias de éxito del reto",
                testimonials: [
                  { name: "Diego Martínez", role: "Freelancer", content: "Nunca pensé que en 5 días tendría mi primer producto digital vendiendo. ¡Superó mis expectativas!", rating: 5 },
                  { name: "Sofía Hernández", role: "Emprendedora", content: "La estructura día a día es perfecta. Te lleva de la mano sin abrumarte.", rating: 5 },
                  { name: "Andrés Ruiz", role: "Coach", content: "El reto me dio la claridad que necesitaba. Ya voy por mi tercer lanzamiento.", rating: 5 }
                ]
              }
            }],
            backgroundColor: "auto",
          },
          {
            id: "ch-s8",
            name: "Formulario del Reto",
            blocks: [{
              id: "ch-b8", type: "form", data: {
                title: "Únete al Reto (es 100% GRATIS)",
                subtitle: "Cupos limitados. Asegura tu lugar ahora.",
                ctaText: "¡Quiero Unirme al Reto! →",
                fields: ["Nombre", "Email", "WhatsApp"]
              }
            }],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(30, 80%, 15%) 0%, hsl(15, 70%, 20%) 100%)",
          },
        ],
      },
      {
        type: "thank-you",
        name: "Bienvenida al Reto",
        sections: [
          {
            id: "ch-p2-s1",
            name: "Bienvenida",
            blocks: [
              {
                id: "ch-p2-b1", type: "hero", data: {
                  title: "¡Bienvenido al Reto de 5 Días! 🏆",
                  subtitle: "Estás a punto de transformar tu idea en un producto real. Revisa tu correo para los primeros pasos.",
                  ctaText: "Unirme al Grupo de WhatsApp",
                }
              },
            ],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(140, 60%, 15%) 0%, hsl(160, 50%, 20%) 100%)",
          },
        ],
      },
    ],
  },

  // ─── 4. LEAD MAGNET / RECURSO GRATUITO ───────────────────────────
  {
    id: "lead-magnet",
    name: "Lead Magnet / Recurso Gratuito",
    description: "Embudo para captar leads con un recurso gratuito de alto valor: ebook, guía, checklist o plantilla.",
    category: "Captación",
    icon: "🧲",
    gradient: "from-emerald-500 to-teal-600",
    tags: ["Lead Gen", "Ebook", "Guía gratuita"],
    suggestedHeadingFont: "Inter, sans-serif",
    suggestedBodyFont: "DM Sans, sans-serif",
    pages: [
      {
        type: "attraction",
        name: "Descarga Gratuita",
        sections: [
          {
            id: "lm-s1",
            name: "Navbar",
            blocks: [{ id: "lm-b1", type: "navbar", data: { logo: "Tu Marca", links: ["Recurso", "Contenido", "Descargar"] } }],
            backgroundColor: "dark",
          },
          {
            id: "lm-s2",
            name: "Hero",
            blocks: [{
              id: "lm-b2", type: "hero", data: {
                title: "Descarga GRATIS: La Guía Definitiva para [Tu Tema]",
                subtitle: "Más de 50 páginas con estrategias probadas, plantillas listas para usar y casos de estudio reales. ¡Sin costo!",
                ctaText: "Descargar Ahora GRATIS →",
                imageUrl: "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=800&auto=format&fit=crop"
              }
            }],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(160, 60%, 12%) 0%, hsl(170, 50%, 18%) 100%)",
          },
          {
            id: "lm-s3",
            name: "Contenido de la Guía",
            blocks: [{
              id: "lm-b3", type: "features", data: {
                title: "¿Qué encontrarás dentro?",
                features: [
                  { icon: "📖", title: "50+ Páginas de Contenido", description: "Estrategias detalladas paso a paso que puedes implementar hoy mismo." },
                  { icon: "📊", title: "10 Plantillas Editables", description: "Templates listos para usar en Canva, Google Docs y Notion." },
                  { icon: "✅", title: "Checklists Prácticos", description: "Listas de verificación para no olvidar ningún paso." },
                  { icon: "🎯", title: "3 Casos de Estudio", description: "Ejemplos reales de personas que aplicaron estas estrategias con éxito." }
                ]
              }
            }],
            backgroundColor: "auto",
          },
          {
            id: "lm-s4",
            name: "Preview del Contenido",
            blocks: [{
              id: "lm-b4", type: "benefits", data: {
                title: "Capítulos incluidos en la guía",
                benefits: [
                  "Capítulo 1: Los fundamentos que nadie te enseña",
                  "Capítulo 2: Estrategia de implementación en 7 pasos",
                  "Capítulo 3: Herramientas y recursos recomendados",
                  "Capítulo 4: Errores comunes y cómo evitarlos",
                  "Capítulo 5: Plan de acción de 30 días",
                  "BONUS: Plantillas y checklists descargables"
                ]
              }
            }],
            backgroundColor: "light",
          },
          {
            id: "lm-s5",
            name: "Prueba Social",
            blocks: [{
              id: "lm-b5", type: "stats", data: {
                stats: [
                  { value: "12,000+", label: "Descargas" },
                  { value: "4.8/5", label: "Calificación" },
                  { value: "50+", label: "Páginas" },
                  { value: "100%", label: "Gratis" }
                ]
              }
            }],
            backgroundColor: "auto",
          },
          {
            id: "lm-s6",
            name: "Testimonios",
            blocks: [{
              id: "lm-b6", type: "testimonials", data: {
                title: "Lo que dicen quienes ya descargaron la guía",
                testimonials: [
                  { name: "Valentina Torres", role: "Marketing Manager", content: "La mejor guía gratuita que he encontrado. Contenido de nivel premium sin costo.", rating: 5 },
                  { name: "Fernando Díaz", role: "Emprendedor", content: "Las plantillas solas valen oro. Me ahorraron semanas de trabajo.", rating: 5 }
                ]
              }
            }],
            backgroundColor: "auto",
          },
          {
            id: "lm-s7",
            name: "Formulario de Descarga",
            blocks: [{
              id: "lm-b7", type: "form", data: {
                title: "Descarga tu guía gratuita ahora",
                subtitle: "Ingresa tus datos y recibirás el enlace de descarga inmediatamente.",
                ctaText: "Enviarme la Guía GRATIS →",
                fields: ["Nombre", "Email"]
              }
            }],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(160, 60%, 12%) 0%, hsl(170, 50%, 18%) 100%)",
          },
        ],
      },
      {
        type: "thank-you",
        name: "Descarga Lista",
        sections: [
          {
            id: "lm-p2-s1",
            name: "Descarga",
            blocks: [
              {
                id: "lm-p2-b1", type: "hero", data: {
                  title: "¡Tu guía está lista! 📚",
                  subtitle: "Hemos enviado el enlace de descarga a tu email. También puedes descargarla directamente desde aquí.",
                  ctaText: "Descargar Ahora",
                }
              },
              {
                id: "lm-p2-b2", type: "cta", data: {
                  title: "¿Quieres ir más profundo?",
                  description: "Si te gustó la guía, nuestro programa completo te llevará al siguiente nivel con mentoría personalizada.",
                  buttonText: "Conocer el Programa Completo →",
                  buttonLink: "#"
                }
              }
            ],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(140, 60%, 15%) 0%, hsl(160, 50%, 20%) 100%)",
          },
        ],
      },
    ],
  },

  // ─── 5. SERVICIOS / CONSULTORÍA ──────────────────────────────────
  {
    id: "consulting",
    name: "Servicios y Consultoría",
    description: "Embudo profesional para vender servicios de consultoría, coaching o servicios profesionales.",
    category: "Servicios",
    icon: "💼",
    gradient: "from-sky-600 to-blue-700",
    tags: ["Coaching", "Consultoría", "Servicios Pro"],
    suggestedHeadingFont: "Cormorant Garamond, serif",
    suggestedBodyFont: "Raleway, sans-serif",
    pages: [
      {
        type: "attraction",
        name: "Página de Servicios",
        sections: [
          {
            id: "co-s1",
            name: "Navbar",
            blocks: [{ id: "co-b1", type: "navbar", data: { logo: "Consultoría Pro", links: ["Servicios", "Resultados", "Sobre Mí", "Agendar"] } }],
            backgroundColor: "dark",
          },
          {
            id: "co-s2",
            name: "Hero",
            blocks: [{
              id: "co-b2", type: "hero", data: {
                title: "Transforma tu negocio con consultoría estratégica personalizada",
                subtitle: "Acompaño a emprendedores y empresas a escalar sus resultados con un plan a medida. +200 clientes satisfechos en 15 industrias.",
                ctaText: "Agendar Llamada Gratuita →",
                imageUrl: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&auto=format&fit=crop"
              }
            }],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(210, 70%, 12%) 0%, hsl(220, 60%, 18%) 100%)",
          },
          {
            id: "co-s3",
            name: "Servicios",
            blocks: [{
              id: "co-b3", type: "features", data: {
                title: "Servicios de Consultoría",
                features: [
                  { icon: "🎯", title: "Diagnóstico Estratégico", description: "Análisis completo de tu negocio con un plan de acción personalizado de 90 días." },
                  { icon: "📈", title: "Acompañamiento Mensual", description: "Sesiones semanales + acceso por chat para resolver dudas en tiempo real." },
                  { icon: "🔧", title: "Implementación Guiada", description: "Te acompaño paso a paso en la ejecución de cada estrategia." },
                  { icon: "👥", title: "Consultoría para Equipos", description: "Capacitación y mentoría para tu equipo de trabajo." }
                ]
              }
            }],
            backgroundColor: "auto",
          },
          {
            id: "co-s4",
            name: "Proceso",
            blocks: [{
              id: "co-b4", type: "timeline", data: {
                title: "¿Cómo funciona?",
                items: [
                  { title: "1. Llamada de Diagnóstico", description: "30 minutos gratuitos para entender tu situación actual y objetivos." },
                  { title: "2. Propuesta Personalizada", description: "Diseño un plan estratégico a medida basado en tu diagnóstico." },
                  { title: "3. Implementación", description: "Trabajamos juntos para ejecutar el plan con sesiones semanales." },
                  { title: "4. Resultados", description: "Medimos, ajustamos y escalamos para maximizar tu ROI." }
                ]
              }
            }],
            backgroundColor: "light",
          },
          {
            id: "co-s5",
            name: "Resultados",
            blocks: [{
              id: "co-b5", type: "stats", data: {
                stats: [
                  { value: "200+", label: "Clientes asesorados" },
                  { value: "15+", label: "Industrias" },
                  { value: "340%", label: "ROI promedio" },
                  { value: "95%", label: "Renuevan servicio" }
                ]
              }
            }],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(210, 70%, 12%) 0%, hsl(220, 60%, 18%) 100%)",
          },
          {
            id: "co-s6",
            name: "Testimonios",
            blocks: [{
              id: "co-b6", type: "testimonials", data: {
                title: "Resultados de clientes reales",
                testimonials: [
                  { name: "Ricardo Morales", role: "CEO, AgenciaPro", content: "En 6 meses triplicamos nuestra facturación. La inversión en consultoría fue la mejor decisión.", rating: 5 },
                  { name: "Isabella Campos", role: "Fundadora, FitBrand", content: "Me ayudó a salir del estancamiento. Ahora tengo un negocio escalable y rentable.", rating: 5 },
                  { name: "Marcos Navarro", role: "Director, TechStart", content: "Claridad, estructura y resultados. Exactamente lo que necesitaba mi empresa.", rating: 5 }
                ]
              }
            }],
            backgroundColor: "auto",
          },
          {
            id: "co-s7",
            name: "Precios",
            blocks: [{
              id: "co-b7", type: "pricing", data: {
                title: "Planes de Consultoría",
                plans: [
                  { name: "Sesión Única", price: "$197", period: "por sesión", features: ["90 min de consultoría 1-a-1", "Plan de acción escrito", "Grabación de la sesión", "1 semana de seguimiento por chat"], ctaText: "Agendar Sesión", highlighted: false },
                  { name: "Pack Mensual", price: "$697", period: "/mes", features: ["4 sesiones mensuales de 60 min", "Soporte por chat ilimitado", "Acceso a plantillas exclusivas", "Revisión semanal de métricas", "Prioridad en agenda"], ctaText: "Empezar Ahora", highlighted: true },
                  { name: "VIP Trimestral", price: "$1,497", period: "/trimestre", features: ["Todo del Pack Mensual", "12 sesiones en 3 meses", "Auditoría completa del negocio", "Consultoría para tu equipo", "Acceso a red de contactos"], ctaText: "Quiero ser VIP", highlighted: false }
                ]
              }
            }],
            backgroundColor: "auto",
          },
          {
            id: "co-s8",
            name: "FAQ",
            blocks: [{
              id: "co-b8", type: "faq", data: {
                title: "Preguntas Frecuentes",
                faqs: [
                  { question: "¿Para qué tipo de negocios funciona?", answer: "Trabajo con emprendedores, freelancers y PYMEs de cualquier industria. Si tienes un negocio y quieres crecer, puedo ayudarte." },
                  { question: "¿Cómo son las sesiones?", answer: "Son sesiones por videollamada donde trabajamos en tu estrategia con ejercicios prácticos y tareas accionables." },
                  { question: "¿Cuánto tiempo toma ver resultados?", answer: "Los primeros resultados suelen verse en 30-60 días. El impacto completo se nota entre 3-6 meses." },
                  { question: "¿Puedo cancelar en cualquier momento?", answer: "Sí, los planes mensuales se pueden cancelar al final de cada periodo sin penalización." }
                ]
              }
            }],
            backgroundColor: "light",
          },
          {
            id: "co-s9",
            name: "CTA Final",
            blocks: [{
              id: "co-b9", type: "cta", data: {
                title: "¿Listo para transformar tu negocio?",
                description: "Agenda una llamada gratuita de 30 minutos y descubre cómo puedo ayudarte a alcanzar tus objetivos.",
                buttonText: "Agendar Mi Llamada GRATIS →",
                buttonLink: "#"
              }
            }],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(210, 70%, 12%) 0%, hsl(220, 60%, 18%) 100%)",
          },
        ],
      },
      {
        type: "thank-you",
        name: "Confirmación de Agenda",
        sections: [
          {
            id: "co-p2-s1",
            name: "Confirmación",
            blocks: [{
              id: "co-p2-b1", type: "hero", data: {
                title: "¡Tu llamada está agendada! 📅",
                subtitle: "Recibirás un correo con la confirmación y el enlace de la videollamada. ¡Nos vemos pronto!",
                ctaText: "Agregar a Mi Calendario",
              }
            }],
            backgroundColor: "gradient",
            backgroundGradient: "linear-gradient(135deg, hsl(140, 60%, 15%) 0%, hsl(160, 50%, 20%) 100%)",
          },
        ],
      },
    ],
  },

  // ─── 6. COACHING / MENTORÍA ─────────────────────────────────────
  {
    id: "coaching",
    name: "Coaching & Mentoría",
    description: "Landing page para coaches y mentores con programa, resultados y reserva de sesión.",
    category: "Coaching",
    icon: "🧠",
    gradient: "from-indigo-500 to-violet-600",
    tags: ["Coaching", "Mentoría", "Transformación"],
    suggestedHeadingFont: "Poppins, sans-serif",
    suggestedBodyFont: "Open Sans, sans-serif",
    pages: [
      {
        type: "attraction",
        name: "Programa de Coaching",
        sections: [
          { id: "co6-s1", name: "Navbar", blocks: [{ id: "co6-b1", type: "navbar", data: { logo: "Coach Pro", links: ["Programa", "Resultados", "Sobre Mí", "Agendar"] } }], backgroundColor: "dark" },
          { id: "co6-s2", name: "Hero", blocks: [{ id: "co6-b2", type: "hero", data: { title: "Desbloquea tu máximo potencial con coaching personalizado", subtitle: "Programa de 12 semanas que ha transformado a más de 500 profesionales. Mentoría 1-a-1 con metodología probada.", ctaText: "Reservar Sesión Gratuita →", variant: 2, imageUrl: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&auto=format&fit=crop" } }], backgroundColor: "gradient", backgroundGradient: "linear-gradient(135deg, hsl(250, 65%, 12%) 0%, hsl(270, 55%, 18%) 100%)" },
          { id: "co6-s3", name: "Beneficios", blocks: [{ id: "co6-b3", type: "benefits", data: { title: "Lo que lograrás con el programa", variant: 3, benefits: ["Claridad total sobre tu propósito y metas profesionales", "Sistema de productividad que triplica tus resultados", "Mentalidad de alto rendimiento y resiliencia", "Red de contactos de alto nivel", "Plan de acción personalizado de 90 días", "Seguimiento semanal con métricas claras"] } }], backgroundColor: "auto" },
          { id: "co6-s4", name: "Proceso", blocks: [{ id: "co6-b4", type: "timeline", data: { title: "Tu camino de transformación", variant: 3, steps: [{ number: "1", title: "Diagnóstico", description: "Sesión inicial para mapear tu situación actual y definir objetivos claros." }, { number: "2", title: "Plan Estratégico", description: "Diseño de tu hoja de ruta personalizada con hitos medibles." }, { number: "3", title: "Ejecución Guiada", description: "Sesiones semanales de coaching con ejercicios prácticos y accountability." }, { number: "4", title: "Resultados", description: "Evaluación de progreso, ajustes y plan de sostenibilidad a largo plazo." }] } }], backgroundColor: "light" },
          { id: "co6-s5", name: "Testimonios", blocks: [{ id: "co6-b5", type: "testimonials", data: { title: "Transformaciones reales", variant: 4, testimonials: [{ name: "Lucía Fernández", role: "VP Marketing", content: "El coaching me dio la claridad y confianza para liderar mi equipo de 30 personas. Ascendí en 4 meses.", rating: 5, type: "text" }, { name: "Manuel Ortiz", role: "Emprendedor", content: "Pasé de estar estancado a facturar 3x en mi negocio. La metodología es poderosa.", rating: 5, type: "text" }, { name: "Sandra Vega", role: "Directora RRHH", content: "El mejor inversión en desarrollo personal que he hecho. Resultados desde la primera sesión.", rating: 5, type: "text" }] } }], backgroundColor: "auto" },
          { id: "co6-s6", name: "Stats", blocks: [{ id: "co6-b6", type: "stats", data: { stats: [{ value: "500+", label: "Clientes transformados" }, { value: "96%", label: "Satisfacción" }, { value: "3x", label: "ROI promedio" }, { value: "12", label: "Semanas del programa" }] } }], backgroundColor: "gradient", backgroundGradient: "linear-gradient(135deg, hsl(250, 65%, 12%) 0%, hsl(270, 55%, 18%) 100%)" },
          { id: "co6-s7", name: "CTA", blocks: [{ id: "co6-b7", type: "cta", data: { title: "¿Listo para tu transformación?", description: "Agenda una sesión gratuita de diagnóstico de 30 minutos.", buttonText: "Agendar Mi Sesión →", variant: 5 } }], backgroundColor: "auto" },
        ],
      },
    ],
  },

  // ─── 7. SAAS ─────────────────────────────────────────────────────
  {
    id: "saas",
    name: "SaaS / App Digital",
    description: "Landing profesional para software, apps y herramientas digitales con features, pricing y demo.",
    category: "SaaS",
    icon: "💻",
    gradient: "from-cyan-500 to-blue-600",
    tags: ["Software", "App", "Tech"],
    suggestedHeadingFont: "Space Grotesk, sans-serif",
    suggestedBodyFont: "Sora, sans-serif",
    pages: [
      {
        type: "attraction",
        name: "Landing SaaS",
        sections: [
          { id: "sa-s1", name: "Navbar", blocks: [{ id: "sa-b1", type: "navbar", data: { logo: "AppFlow", links: ["Features", "Pricing", "Demo", "Login"] } }], backgroundColor: "dark" },
          { id: "sa-s2", name: "Hero", blocks: [{ id: "sa-b2", type: "hero", data: { title: "La plataforma todo-en-uno que automatiza tu negocio", subtitle: "Ahorra 10+ horas semanales con automatizaciones inteligentes, analytics en tiempo real y colaboración sin fricciones.", ctaText: "Prueba Gratis 14 Días →", variant: 5 } }], backgroundColor: "gradient", backgroundGradient: "linear-gradient(135deg, hsl(200, 70%, 8%) 0%, hsl(210, 65%, 15%) 100%)" },
          { id: "sa-s3", name: "Features", blocks: [{ id: "sa-b3", type: "features", data: { title: "Todo lo que necesitas en un solo lugar", variant: 5, features: [{ icon: "⚡", title: "Automatizaciones", description: "Flujos de trabajo inteligentes que eliminan tareas repetitivas." }, { icon: "📊", title: "Analytics Avanzados", description: "Dashboards en tiempo real con métricas que importan." }, { icon: "🔗", title: "Integraciones", description: "Conecta con +200 herramientas que ya usas." }, { icon: "🛡️", title: "Seguridad Enterprise", description: "Encriptación end-to-end y cumplimiento SOC2." }, { icon: "👥", title: "Colaboración", description: "Espacios de trabajo compartidos con roles y permisos." }, { icon: "🚀", title: "API Abierta", description: "Documetación completa para personalizar todo." }] } }], backgroundColor: "auto" },
          { id: "sa-s4", name: "Stats", blocks: [{ id: "sa-b4", type: "stats", data: { stats: [{ value: "10,000+", label: "Equipos activos" }, { value: "99.9%", label: "Uptime garantizado" }, { value: "4.9/5", label: "Rating en G2" }, { value: "< 2min", label: "Setup inicial" }] } }], backgroundColor: "auto" },
          { id: "sa-s5", name: "Testimonios", blocks: [{ id: "sa-b5", type: "testimonials", data: { title: "Empresas que confían en nosotros", variant: 5, testimonials: [{ name: "Elena Ríos", role: "CTO, TechScale", content: "Reducimos nuestro tiempo de deploy en un 70%. La integración fue impecable.", rating: 5, type: "text" }, { name: "Pedro Sánchez", role: "Head of Ops", content: "La mejor herramienta que hemos adoptado este año. ROI positivo en el primer mes.", rating: 5, type: "text" }] } }], backgroundColor: "light" },
          { id: "sa-s6", name: "Pricing", blocks: [{ id: "sa-b6", type: "pricing", data: { title: "Planes simples, sin sorpresas", plans: [{ name: "Starter", price: "$29", period: "/mes", features: ["3 usuarios", "5 automatizaciones", "Analytics básico", "Soporte email"], ctaText: "Empezar Gratis", highlighted: false }, { name: "Pro", price: "$79", period: "/mes", features: ["15 usuarios", "Automatizaciones ilimitadas", "Analytics avanzado", "Integraciones premium", "Soporte prioritario"], ctaText: "Prueba Gratis", highlighted: true }, { name: "Enterprise", price: "Custom", period: "", features: ["Usuarios ilimitados", "SSO/SAML", "SLA dedicado", "Account manager", "API personalizada"], ctaText: "Contactar Ventas", highlighted: false }] } }], backgroundColor: "auto" },
          { id: "sa-s7", name: "FAQ", blocks: [{ id: "sa-b7", type: "faq", data: { title: "Preguntas Frecuentes", faqs: [{ question: "¿Puedo probar gratis?", answer: "Sí, ofrecemos 14 días de prueba gratuita sin tarjeta de crédito." }, { question: "¿Es fácil migrar desde otra herramienta?", answer: "Sí, tenemos asistentes de migración y soporte dedicado para el proceso." }, { question: "¿Qué pasa si necesito más usuarios?", answer: "Puedes escalar tu plan en cualquier momento desde tu dashboard." }] } }], backgroundColor: "auto" },
          { id: "sa-s8", name: "CTA", blocks: [{ id: "sa-b8", type: "cta", data: { title: "Empieza hoy, escala mañana", description: "14 días gratis. Sin tarjeta de crédito. Setup en menos de 2 minutos.", buttonText: "Crear Mi Cuenta Gratis →", variant: 2 } }], backgroundColor: "gradient", backgroundGradient: "linear-gradient(135deg, hsl(200, 70%, 8%) 0%, hsl(210, 65%, 15%) 100%)" },
        ],
      },
    ],
  },

  // ─── 8. ECOMMERCE ────────────────────────────────────────────────
  {
    id: "ecommerce",
    name: "Ecommerce / Producto",
    description: "Landing de venta de producto físico o digital con galería, beneficios y checkout optimizado.",
    category: "Ecommerce",
    icon: "🛍️",
    gradient: "from-orange-500 to-red-600",
    tags: ["Producto", "Tienda", "Ventas"],
    suggestedHeadingFont: "Montserrat, sans-serif",
    suggestedBodyFont: "DM Sans, sans-serif",
    pages: [
      {
        type: "attraction",
        name: "Página de Producto",
        sections: [
          { id: "ec-s1", name: "Navbar", blocks: [{ id: "ec-b1", type: "navbar", data: { logo: "BrandStore", links: ["Producto", "Beneficios", "Reviews", "Comprar"] } }], backgroundColor: "dark" },
          { id: "ec-s2", name: "Hero", blocks: [{ id: "ec-b2", type: "hero", data: { title: "El producto que tu rutina necesitaba", subtitle: "Diseño premium, materiales de alta calidad y envío gratis en 24h. +50,000 clientes satisfechos.", ctaText: "Comprar Ahora — 30% OFF →", variant: 2, imageUrl: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=800&auto=format&fit=crop" } }], backgroundColor: "gradient", backgroundGradient: "linear-gradient(135deg, hsl(25, 80%, 10%) 0%, hsl(15, 70%, 16%) 100%)" },
          { id: "ec-s3", name: "Beneficios", blocks: [{ id: "ec-b3", type: "benefits", data: { title: "¿Por qué elegir nuestro producto?", variant: 4, benefits: ["Materiales premium de grado A+", "Envío gratis en 24h a todo el país", "Garantía de 2 años incluida", "Diseño ergonómico y moderno", "+50,000 clientes satisfechos", "Devolución sin preguntas en 30 días"] } }], backgroundColor: "auto" },
          { id: "ec-s4", name: "Features", blocks: [{ id: "ec-b4", type: "features", data: { title: "Características que lo hacen único", variant: 2, features: [{ icon: "✨", title: "Diseño Premium", description: "Cada detalle pensado para la máxima elegancia y funcionalidad." }, { icon: "🔋", title: "Larga Duración", description: "Materiales resistentes que mantienen su calidad por años." }, { icon: "🌱", title: "Eco-Friendly", description: "Fabricado con procesos sostenibles y materiales reciclables." }, { icon: "📦", title: "Packaging Especial", description: "Empaque de regalo incluido, perfecto para obsequiar." }] } }], backgroundColor: "light" },
          { id: "ec-s5", name: "Testimonios", blocks: [{ id: "ec-b5", type: "testimonials", data: { title: "Lo que dicen nuestros clientes", variant: 3, testimonials: [{ name: "Laura Méndez", role: "Cliente Verificada", content: "La calidad es increíble, superó mis expectativas. Ya pedí 3 más para regalar.", rating: 5, type: "text" }, { name: "Javier Torres", role: "Cliente Verificado", content: "Llegó en 24 horas como prometieron. Excelente servicio y producto impecable.", rating: 5, type: "text" }, { name: "Camila Ruiz", role: "Cliente Verificada", content: "El mejor producto que he comprado este año. 10/10", rating: 5, type: "text" }] } }], backgroundColor: "auto" },
          { id: "ec-s6", name: "CTA", blocks: [{ id: "ec-b6", type: "cta", data: { title: "Oferta limitada: 30% de descuento", description: "Solo quedan pocas unidades a este precio. Envío gratis incluido.", buttonText: "Comprar Ahora →", variant: 4 } }], backgroundColor: "gradient", backgroundGradient: "linear-gradient(135deg, hsl(25, 80%, 10%) 0%, hsl(15, 70%, 16%) 100%)" },
        ],
      },
    ],
  },

  // ─── 9. FITNESS / GYM ───────────────────────────────────────────
  {
    id: "fitness",
    name: "Fitness & Bienestar",
    description: "Landing para programas de entrenamiento, retos fitness y membresías de gimnasio.",
    category: "Fitness",
    icon: "💪",
    gradient: "from-green-500 to-emerald-600",
    tags: ["Gym", "Entrenamiento", "Salud"],
    suggestedHeadingFont: "Oswald, sans-serif",
    suggestedBodyFont: "Roboto, sans-serif",
    pages: [
      {
        type: "attraction",
        name: "Programa Fitness",
        sections: [
          { id: "fi-s1", name: "Navbar", blocks: [{ id: "fi-b1", type: "navbar", data: { logo: "FitPro Academy", links: ["Programa", "Resultados", "Coach", "Unirme"] } }], backgroundColor: "dark" },
          { id: "fi-s2", name: "Hero", blocks: [{ id: "fi-b2", type: "hero", data: { title: "Transforma tu cuerpo en 90 días con entrenamiento personalizado", subtitle: "Plan nutricional + rutinas adaptadas a tu nivel + coaching semanal. Sin gimnasio, desde casa o donde estés.", ctaText: "Empezar Mi Transformación →", variant: 3, imageUrl: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&auto=format&fit=crop" } }], backgroundColor: "gradient", backgroundGradient: "linear-gradient(135deg, hsl(150, 60%, 8%) 0%, hsl(160, 50%, 14%) 100%)" },
          { id: "fi-s3", name: "Timeline", blocks: [{ id: "fi-b3", type: "timeline", data: { title: "Tu plan de 90 días", variant: 1, steps: [{ number: "1", title: "Fase de Activación (Días 1-30)", description: "Establece hábitos, aprende la técnica correcta y activa tu metabolismo." }, { number: "2", title: "Fase de Aceleración (Días 31-60)", description: "Incrementa intensidad, construye masa muscular y quema grasa." }, { number: "3", title: "Fase de Transformación (Días 61-90)", description: "Resultados visibles, definición muscular y nuevo estilo de vida." }] } }], backgroundColor: "auto" },
          { id: "fi-s4", name: "Features", blocks: [{ id: "fi-b4", type: "features", data: { title: "Qué incluye tu programa", variant: 1, features: [{ icon: "🏋️", title: "Rutinas Personalizadas", description: "Entrenamientos adaptados a tu nivel, equipo y objetivos." }, { icon: "🥗", title: "Plan Nutricional", description: "Menús semanales con recetas fáciles y lista de compras." }, { icon: "📱", title: "App de Seguimiento", description: "Registra tus entrenamientos, progreso y métricas." }, { icon: "💬", title: "Coaching Semanal", description: "Sesiones de seguimiento para ajustar tu plan." }] } }], backgroundColor: "light" },
          { id: "fi-s5", name: "Testimonios", blocks: [{ id: "fi-b5", type: "testimonials", data: { title: "Transformaciones que inspiran", variant: 2, testimonials: [{ name: "Roberto Silva", role: "Perdió 15kg", content: "En 90 días cambié completamente. No solo perdí peso, gané confianza y energía.", rating: 5, type: "text" }, { name: "Daniela Cruz", role: "Ganó masa muscular", content: "El plan nutricional fue un game-changer. Nunca me sentí con hambre y los resultados son increíbles.", rating: 5, type: "text" }] } }], backgroundColor: "auto" },
          { id: "fi-s6", name: "Stats", blocks: [{ id: "fi-b6", type: "stats", data: { stats: [{ value: "2,000+", label: "Transformaciones" }, { value: "-12kg", label: "Pérdida promedio" }, { value: "97%", label: "Completan el programa" }, { value: "4.9/5", label: "Calificación" }] } }], backgroundColor: "gradient", backgroundGradient: "linear-gradient(135deg, hsl(150, 60%, 8%) 0%, hsl(160, 50%, 14%) 100%)" },
          { id: "fi-s7", name: "CTA", blocks: [{ id: "fi-b7", type: "cta", data: { title: "Tu transformación empieza hoy", description: "Plazas limitadas para coaching personalizado. Garantía de 30 días.", buttonText: "Comenzar Ahora →", variant: 5 } }], backgroundColor: "auto" },
        ],
      },
    ],
  },

  // ─── 10. SERVICIOS PROFESIONALES ────────────────────────────────
  {
    id: "services-pro",
    name: "Servicios Profesionales",
    description: "Landing para agencias, freelancers y profesionales que ofrecen servicios especializados.",
    category: "Servicios",
    icon: "🔧",
    gradient: "from-slate-600 to-gray-800",
    tags: ["Agencia", "Freelance", "Profesional"],
    suggestedHeadingFont: "Cormorant Garamond, serif",
    suggestedBodyFont: "Raleway, sans-serif",
    pages: [
      {
        type: "attraction",
        name: "Servicios",
        sections: [
          { id: "sp-s1", name: "Navbar", blocks: [{ id: "sp-b1", type: "navbar", data: { logo: "StudioPro", links: ["Servicios", "Portfolio", "Proceso", "Contacto"] } }], backgroundColor: "dark" },
          { id: "sp-s2", name: "Hero", blocks: [{ id: "sp-b2", type: "hero", data: { title: "Diseño & desarrollo digital que convierte visitantes en clientes", subtitle: "Agencia boutique con +8 años creando experiencias digitales de alto impacto para marcas ambiciosas.", ctaText: "Ver Nuestro Trabajo →", variant: 4 } }], backgroundColor: "gradient", backgroundGradient: "linear-gradient(135deg, hsl(220, 20%, 8%) 0%, hsl(230, 25%, 14%) 100%)" },
          { id: "sp-s3", name: "Features", blocks: [{ id: "sp-b3", type: "features", data: { title: "Nuestros servicios", variant: 3, features: [{ icon: "🎨", title: "Diseño UI/UX", description: "Interfaces intuitivas que enamoran a tus usuarios y aumentan conversiones." }, { icon: "💻", title: "Desarrollo Web", description: "Sitios rápidos, responsivos y optimizados para SEO y rendimiento." }, { icon: "📱", title: "Apps Móviles", description: "Aplicaciones nativas e híbridas con experiencia premium." }, { icon: "📈", title: "Growth Marketing", description: "Estrategias de crecimiento basadas en datos y resultados medibles." }] } }], backgroundColor: "auto" },
          { id: "sp-s4", name: "Proceso", blocks: [{ id: "sp-b4", type: "timeline", data: { title: "Nuestro proceso", variant: 4, steps: [{ number: "01", title: "Discovery", description: "Entendemos tu negocio, audiencia y objetivos para definir la estrategia." }, { number: "02", title: "Design", description: "Creamos wireframes y diseños pixel-perfect aprobados por ti." }, { number: "03", title: "Develop", description: "Construimos con código limpio, testing riguroso y mejores prácticas." }, { number: "04", title: "Launch", description: "Deploy, optimización y soporte post-lanzamiento incluido." }] } }], backgroundColor: "light" },
          { id: "sp-s5", name: "Testimonios", blocks: [{ id: "sp-b5", type: "testimonials", data: { title: "Lo que dicen nuestros clientes", variant: 1, testimonials: [{ name: "Andrea Morales", role: "CEO, FinTechApp", content: "Entregaron un producto excepcional en tiempo récord. La comunicación fue impecable.", rating: 5, type: "text" }, { name: "Diego Herrera", role: "Fundador, EduPlatform", content: "Nuestra tasa de conversión subió 180% con el nuevo diseño. Inversión que se paga sola.", rating: 5, type: "text" }, { name: "Isabel Navarro", role: "CMO, RetailBrand", content: "Profesionalismo de primer nivel. Ya estamos planeando el siguiente proyecto juntos.", rating: 5, type: "text" }] } }], backgroundColor: "auto" },
          { id: "sp-s6", name: "CTA", blocks: [{ id: "sp-b6", type: "cta", data: { title: "¿Tienes un proyecto en mente?", description: "Agenda una llamada de 30 min para discutir tu visión. Sin compromiso.", buttonText: "Agendar Llamada →", variant: 3 } }], backgroundColor: "gradient", backgroundGradient: "linear-gradient(135deg, hsl(220, 20%, 8%) 0%, hsl(230, 25%, 14%) 100%)" },
        ],
      },
    ],
  },

  // ─── 11. HEALTH / BIENESTAR ─────────────────────────────────────
  {
    id: "health",
    name: "Salud & Bienestar",
    description: "Landing para profesionales de la salud, nutricionistas, terapeutas y programas de bienestar.",
    category: "Salud",
    icon: "🌿",
    gradient: "from-teal-500 to-green-600",
    tags: ["Salud", "Nutrición", "Bienestar"],
    suggestedHeadingFont: "Outfit, sans-serif",
    suggestedBodyFont: "Nunito, sans-serif",
    pages: [
      {
        type: "attraction",
        name: "Programa de Bienestar",
        sections: [
          { id: "he-s1", name: "Navbar", blocks: [{ id: "he-b1", type: "navbar", data: { logo: "VidaPlena", links: ["Programa", "Beneficios", "Testimonios", "Comenzar"] } }], backgroundColor: "dark" },
          { id: "he-s2", name: "Hero", blocks: [{ id: "he-b2", type: "hero", data: { title: "Recupera tu energía y bienestar con un plan integral de salud", subtitle: "Programa personalizado de nutrición, mindfulness y hábitos saludables diseñado por profesionales certificados.", ctaText: "Empezar Mi Camino →", variant: 1 } }], backgroundColor: "gradient", backgroundGradient: "linear-gradient(135deg, hsl(170, 50%, 10%) 0%, hsl(150, 45%, 16%) 100%)" },
          { id: "he-s3", name: "Beneficios", blocks: [{ id: "he-b3", type: "benefits", data: { title: "Beneficios del programa", variant: 1, benefits: ["Plan nutricional personalizado por nutriólogos certificados", "Sesiones semanales de mindfulness y meditación guiada", "Rutinas de movimiento adaptadas a tu condición física", "Seguimiento de biomarcadores y métricas de salud", "Comunidad de apoyo con personas en tu mismo camino", "Acceso a recetario con +200 recetas saludables"] } }], backgroundColor: "auto" },
          { id: "he-s4", name: "Timeline", blocks: [{ id: "he-b4", type: "timeline", data: { title: "Tu camino hacia el bienestar", variant: 2, steps: [{ number: "1", title: "Evaluación Integral", description: "Análisis completo de tu estado de salud, hábitos y objetivos." }, { number: "2", title: "Plan Personalizado", description: "Diseño de tu programa con nutrición, ejercicio y mindfulness." }, { number: "3", title: "Acompañamiento", description: "Sesiones quincenales con tu profesional de salud asignado." }, { number: "4", title: "Resultados Sostenibles", description: "Nuevo estilo de vida con hábitos que perduran." }] } }], backgroundColor: "light" },
          { id: "he-s5", name: "Testimonios", blocks: [{ id: "he-b5", type: "testimonials", data: { title: "Historias de transformación", variant: 2, testimonials: [{ name: "Patricia Vega", role: "Recuperó su energía", content: "Después de años sintiéndome agotada, este programa me devolvió la vitalidad. Me siento 10 años más joven.", rating: 5, type: "text" }, { name: "Ricardo Luna", role: "Mejoró su salud", content: "Mis análisis de sangre mejoraron dramáticamente. Mi doctor está impresionado con los resultados.", rating: 5, type: "text" }] } }], backgroundColor: "auto" },
          { id: "he-s6", name: "FAQ", blocks: [{ id: "he-b6", type: "faq", data: { title: "Preguntas Frecuentes", faqs: [{ question: "¿Necesito ir a un consultorio?", answer: "No, todo el programa es virtual. Las consultas son por videollamada." }, { question: "¿Funciona si tengo alguna condición médica?", answer: "Sí, adaptamos el programa a tu condición. Siempre coordinamos con tu médico tratante." }, { question: "¿Cuánto dura el programa?", answer: "El programa base es de 12 semanas, con opción de extensión según tus necesidades." }] } }], backgroundColor: "auto" },
          { id: "he-s7", name: "CTA", blocks: [{ id: "he-b7", type: "cta", data: { title: "Tu bienestar comienza con un paso", description: "Agenda tu evaluación gratuita y descubre tu plan personalizado.", buttonText: "Agendar Evaluación Gratis →", variant: 2 } }], backgroundColor: "gradient", backgroundGradient: "linear-gradient(135deg, hsl(170, 50%, 10%) 0%, hsl(150, 45%, 16%) 100%)" },
        ],
      },
    ],
  },
];
